import React, { useMemo, useState } from 'react'
import type { CSSProperties } from 'react'
import type { StackedHorizontalBarProps } from './BarChart.types'
import {
  BAR_CHART_DEFAULT_TITLE,
  BAR_CHART_DEFAULT_VALUE_LABEL,
  BAR_CHART_HORIZONTAL_BAR_HEIGHT,
  BAR_CHART_HORIZONTAL_DEFAULT_SEGMENTS,
  BAR_CHART_HORIZONTAL_STACKED_WIDTH,
} from './BarChart.types'
import { ChartTooltip } from '../ChartTooltip'
import './BarChart.css'

const StackedHorizontalBar: React.FC<StackedHorizontalBarProps> = ({
  title = BAR_CHART_DEFAULT_TITLE,
  hideTitle = false,
  valueLabel = BAR_CHART_DEFAULT_VALUE_LABEL,
  hideValue = false,
  barHeight = BAR_CHART_HORIZONTAL_BAR_HEIGHT,
  segments = BAR_CHART_HORIZONTAL_DEFAULT_SEGMENTS,
  width = BAR_CHART_HORIZONTAL_STACKED_WIDTH,
  showTooltip = true,
  formatValue,
  darkMode = false,
  className = '',
  style,
  'aria-label': ariaLabel,
}) => {
  const [hover, setHover] = useState<{ x: number; y: number } | null>(null)
  const format = formatValue ?? ((v: number) => String(v))

  const resolvedSegments = useMemo(
    () =>
      segments.length > 0 ? segments : BAR_CHART_HORIZONTAL_DEFAULT_SEGMENTS,
    [segments],
  )

  const classNames = [
    'ds-bar-chart',
    'ds-bar-chart--horizontal',
    'ds-bar-chart--stacked-horizontal',
    showTooltip ? 'ds-bar-chart--interactive' : '',
    darkMode ? 'ds-bar-chart--dark' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ')

  const rootStyle = {
    ...style,
    width: `${width}px`,
    '--ds-bar-chart-bar-height': `${barHeight}px`,
  } as CSSProperties

  const accessibleLabel = [title, valueLabel].filter(Boolean).join(', ')

  return (
    <div
      className={classNames}
      style={rootStyle}
      role="img"
      aria-label={ariaLabel ?? accessibleLabel}
      onMouseEnter={
        showTooltip
          ? (event) => {
              const rect = event.currentTarget.getBoundingClientRect()
              setHover({ x: Math.min(rect.width / 2, 160), y: 4 })
            }
          : undefined
      }
      onMouseLeave={showTooltip ? () => setHover(null) : undefined}
    >
      {!hideTitle ? <p className="ds-bar-chart__title">{title}</p> : null}
      <div className="ds-bar-chart__track-group">
        <div className="ds-bar-chart__bar" aria-hidden="true">
          {resolvedSegments.map((segment, index) => {
            const grow = Number.isFinite(segment.value)
              ? Math.max(0, segment.value)
              : 0
            const segmentStyle = {
              '--ds-bar-chart-segment-grow': grow,
              '--ds-bar-chart-segment-color':
                segment.color ?? 'var(--ds-blue-600)',
            } as CSSProperties

            return (
              <div
                key={`${segment.label ?? 'segment'}-${index}`}
                className="ds-bar-chart__segment"
                style={segmentStyle}
                title={segment.label}
              />
            )
          })}
        </div>
        {!hideValue ? (
          <p className="ds-bar-chart__value">{valueLabel}</p>
        ) : null}
      </div>
      {showTooltip && hover ? (
        <ChartTooltip
          visible
          darkMode={darkMode}
          title={title}
          x={hover.x}
          y={hover.y}
          items={resolvedSegments.map((segment, index) => ({
            label: segment.label ?? `Segment ${index + 1}`,
            value: format(
              Number.isFinite(segment.value) ? Math.max(0, segment.value) : 0,
            ),
            color: segment.color ?? 'var(--ds-blue-600)',
          }))}
        />
      ) : null}
    </div>
  )
}

export default StackedHorizontalBar
